import { MediaText, TextWrapping } from 'editor/src/store/design/types';
import { TextAlign } from 'editor/src/store/fonts/types';
import isFontFileLoaded from 'editor/src/store/fonts/utils/isFontFileLoaded';

import FabricPathText from 'editor/src/fabric/FabricPathText';
import { convFn } from 'editor/src/util/convFn';
import limitPrecision from 'editor/src/util/limitPrecision';
import mm2pt from 'editor/src/util/mm2pt';

import getCustomFontContentWidth from './getCustomFontContentWidth';
import textElementDataToFabricProps from './textElementDataToFabricProps';

const identity: convFn = (x) => x;

export function getRenderedTextObject(textElement: MediaText) {
  return new FabricPathText(textElement.extra.text, {
    ...textElementDataToFabricProps(textElement, 0, identity, false, false, textElement.extra.fontFamily),
    width: textElement.width,
    height: textElement.height,
    left: 0,
    top: 0,
    angle: 0,
    objectCaching: false,
  });
}

export function getFlexbileTextMaxWidth(
  eltWidth: number,
  eltX: number,
  textAlign: TextAlign | undefined,
  pageLeft: number,
  areaWidth: number,
) {
  let maxWidth = areaWidth;
  const elementX = eltX - pageLeft;
  if (textAlign === 'left') {
    maxWidth = areaWidth - elementX;
  } else if (textAlign === 'right') {
    maxWidth = elementX + eltWidth;
  } else {
    maxWidth = Math.min(areaWidth - elementX + eltWidth / 2, elementX + eltWidth / 2) * 2;
  }
  return Math.max(maxWidth, eltWidth);
}

export function extractTextProperties(
  textElement: MediaText,
  container: { x: number; width: number } | undefined,
  recalculateTextWidth?: boolean,
) {
  const { flexibleWidth, fontSize } = textElement.extra;
  const useContentWidth = recalculateTextWidth || flexibleWidth;
  const fabricObject = new FabricPathText(textElement.extra.text, {
    ...textElementDataToFabricProps(textElement, 0, identity, false, false, textElement.extra.fontFamily),
    width: textElement.width,
    height: textElement.height,
    left: 0,
    top: 0,
    angle: 0,
    objectCaching: false,
    flexibleWidth: false,
    recalculateTextWidth: useContentWidth,
    ...(useContentWidth ? { wrapping: TextWrapping.Wrap } : {}),
  });

  // fabric has 14 digits precision internally
  let width = limitPrecision(fabricObject.width ?? 0, 13);
  const height = limitPrecision(fabricObject.height ?? 0, 13);

  if (useContentWidth) {
    // Re-measuring needs the font's glyph metrics. On storefronts custom
    // (uploaded) fonts are not loaded client-side, so calcTextWidth() returns ~0
    // and the box collapses to the quarter-line margin (a tiny sliver) — this is
    // what makes reflow / flexibleWidth ("dynamic size") text tiny.
    //
    // When the font is NOT loaded we cannot measure at all, so NEVER recompute —
    // keep the existing width. This is critical for the empty-text case: clearing
    // the field would otherwise drop the width to the margin (getCustomFontContentWidth
    // only guards non-empty text), and since the font can't be measured, retyping
    // never recovers the width. When the font IS loaded, measure normally
    // (getCustomFontContentWidth also rejects a non-empty ~0 measurement).
    if (isFontFileLoaded(textElement.extra.fontFamily)) {
      const contentWidth = getCustomFontContentWidth(fabricObject, (value) => value);
      width = limitPrecision(contentWidth ?? textElement.width, 13);
    } else {
      width = limitPrecision(textElement.width, 13);
    }
  }

  const offsetLeft = fabricObject.left || 0;
  const offsetTop = fabricObject.top || 0;

  return {
    width,
    height,
    sx: 1,
    sy: 1,
    fontSize: fabricObject.fontSize !== undefined ? mm2pt(fabricObject.fontSize) : fontSize,
    x: (textElement.x || 0) + offsetLeft,
    y: (textElement.y || 0) + offsetTop,
  };
}

function updateTextElementWithoutRender(textElement: MediaText, container: { x: number; width: number } | undefined) {
  const { width, height, sx, sy, fontSize } = extractTextProperties(textElement, container);
  textElement.width = width;
  textElement.height = height;
  textElement.sx = sx;
  textElement.sy = sy;
  textElement.extra.fontSize = fontSize;
}

export default updateTextElementWithoutRender;
